import { useQuery } from '@tanstack/react-query'
import { FileText, Settings2 } from 'lucide-react'
import { templatesQueryOptions } from '@/utils/templateQueryOptions'
import type { MessageTemplate } from '@/types/templateTypes'
import { useShellStore } from '@/stores/shellStore'
import { useUserStore } from '@/stores/userStore'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * Dropdown of the shared message templates. Used above the broadcast message
 * box and inside the reply bar so a canned message can be inserted instead of
 * retyped. Super-admins also get a shortcut into the manage dialog.
 */
export function TemplatePicker({
  onSelect,
  variant = 'button',
  className,
}: {
  onSelect: (template: MessageTemplate) => void
  /** 'button' = labelled pill (composer); 'icon' = compact square (reply bar). */
  variant?: 'button' | 'icon'
  className?: string
}) {
  const { data, status } = useQuery(templatesQueryOptions())
  const isSuperAdmin = useUserStore((s) => s.User?.isSuperAdmin ?? false)
  const openTemplates = useShellStore((s) => s.openTemplates)

  const templates = data?.templates ?? []

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Insert a template"
          title="Insert a template"
          className={cn(
            'inline-flex flex-none items-center justify-center border border-border bg-surface-2 font-semibold text-foreground transition-colors hover:bg-surface',
            variant === 'icon'
              ? 'h-11 w-11 rounded-full'
              : 'gap-1.5 rounded-[9px] px-3 py-1.5 text-[12.5px]',
            className,
          )}
        >
          <FileText className={variant === 'icon' ? 'h-[18px] w-[18px]' : 'h-3.5 w-3.5'} />
          {variant === 'button' && 'Templates'}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="max-h-[320px] w-72 overflow-y-auto">
        <DropdownMenuLabel>Templates</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {status === 'pending' ? (
          <div className="px-2 py-3 text-sm text-muted-foreground">Loading…</div>
        ) : status === 'error' ? (
          <div className="px-2 py-3 text-sm text-destructive">Couldn’t load templates</div>
        ) : templates.length === 0 ? (
          <div className="px-2 py-3 text-sm text-muted-foreground">
            No templates yet.
            {isSuperAdmin && ' Add one below.'}
          </div>
        ) : (
          templates.map((t) => (
            <DropdownMenuItem
              key={t.id}
              onSelect={() => onSelect(t)}
              className="cursor-pointer flex-col items-start gap-0.5"
            >
              <span className="flex w-full items-center gap-1.5 text-sm font-semibold">
                <span className="min-w-0 flex-1 truncate">{t.name}</span>
                {t.priority === 'urgent' && (
                  <span className="flex-none rounded-full bg-destructive/10 px-1.5 py-px text-[10px] font-bold uppercase text-destructive">
                    Urgent
                  </span>
                )}
              </span>
              <span className="line-clamp-2 w-full text-xs text-muted-foreground">{t.body}</span>
            </DropdownMenuItem>
          ))
        )}

        {isSuperAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={openTemplates} className="cursor-pointer">
              <Settings2 className="mr-2 h-4 w-4" />
              Manage templates
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
